import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import type { WearableSleepSummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import { toChartSeries } from '../helpers/series';
import MetricValue from './MetricValue';
import MetricChart from './MetricChart';
import ProviderDeviceChip from './ProviderDeviceChip';

interface SleepMetric {
  key: keyof WearableSleepSummary;
  label: string;
  unit?: string;
}

const SLEEP_METRICS: SleepMetric[] = [
  {
    key: 'durationMinutes',
    label: 'Sleep duration',
    unit: 'min',
  },
  {
    key: 'timeInBedMinutes',
    label: 'Time in bed',
    unit: 'min',
  },
  {
    key: 'efficiencyPercent',
    label: 'Sleep efficiency',
    unit: '%',
  },
  {
    key: 'awakeMinutes',
    label: 'Awake time',
    unit: 'min',
  },
  {
    key: 'lightMinutes',
    label: 'Light sleep',
    unit: 'min',
  },
  {
    key: 'deepMinutes',
    label: 'Deep sleep',
    unit: 'min',
  },
  {
    key: 'remMinutes',
    label: 'REM sleep',
    unit: 'min',
  },
  {
    key: 'interruptionsCount',
    label: 'Interruptions',
  },
  { key: 'napCount', label: 'Naps' },
  {
    key: 'napDurationMinutes',
    label: 'Nap duration',
    unit: 'min',
  },
  {
    key: 'avgHeartRateBpm',
    label: 'Sleep HR (avg)',
    unit: 'bpm',
  },
  {
    key: 'avgHrvSdnnMs',
    label: 'HRV (avg)',
    unit: 'ms',
  },
  {
    key: 'avgRespiratoryRate',
    label: 'Respiratory rate',
    unit: 'br/min',
  },
  {
    key: 'avgSpo2Percent',
    label: 'SpO₂ (avg)',
    unit: '%',
  },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  sleepSummary: WearableSleepSummary[];
}

const SleepSummarySection: React.FC<Props> = ({ range, loading, error, sleepSummary }) => {
  const dayRow = sleepSummary[sleepSummary.length - 1] ?? null;

  return (
    <Card className="mb-4" data-testid="sleep-summary-section">
      <CardHeader>Sleep</CardHeader>
      <CardBody>
        {loading && !dayRow ? <Spinner /> : null}
        {error ? (
          <div data-testid="sleep-summary-error" className="text-danger">
            Could not load section: {error.message}
          </div>
        ) : null}
        {!loading && !error && sleepSummary.length === 0 ? (
          <div data-testid="sleep-summary-empty">
            {range.isSingleDay ? 'No data for the selected day' : 'No data for selected period'}
          </div>
        ) : null}
        {range.isSingleDay && dayRow
          ? SLEEP_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                label={metric.label}
                unit={metric.unit}
                value={dayRow[metric.key] as number | null}
                provider={dayRow.provider}
                device={dayRow.device}
              />
            ))
          : null}
        {!range.isSingleDay && sleepSummary.length > 0 ? (
          <>
            {(() => {
              const lastWithProvider = [...sleepSummary].reverse().find((row) => row.provider);
              return lastWithProvider ? (
                <div className="mb-3">
                  <ProviderDeviceChip
                    provider={lastWithProvider.provider}
                    device={lastWithProvider.device}
                  />
                </div>
              ) : null;
            })()}
            {SLEEP_METRICS.map((metric) => (
              <div key={metric.key as string} className="mb-4">
                <small className="text-muted d-block mb-1">{metric.label}</small>
                <MetricChart
                  points={toChartSeries(
                    sleepSummary,
                    range,
                    (row) => row[metric.key] as number | null
                  )}
                  ariaLabel={metric.label}
                  unit={metric.unit}
                />
              </div>
            ))}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default SleepSummarySection;
export { SLEEP_METRICS };
